/**
 * In-app browser (WebView) detection.
 *
 * Facebook, Instagram, TikTok, etc. open links in their own embedded WebView
 * which strips browser APIs we depend on — most importantly the Notification
 * API. Web push is impossible inside these WebViews, so we detect them and
 * tell the user to open the page in their phone's real browser.
 *
 * UA-string detection only — there is no programmatic API to ask "am I in a
 * WebView" reliably, but the major in-app browsers all tag their UA. Patterns
 * verified against current Facebook/Instagram/TikTok/Snapchat/Twitter/LinkedIn/
 * Pinterest in-app browsers as of 2026-05-01.
 */

export type InAppBrowserKind =
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'snapchat'
  | 'twitter'
  | 'linkedin'
  | 'pinterest'
  | null;

export function detectInAppBrowser(): InAppBrowserKind {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  // Facebook tags FBAN (FB Application Name) + FBAV (FB Application Version).
  if (/FBAN|FBAV/i.test(ua)) return 'facebook';
  if (/Instagram/i.test(ua)) return 'instagram';
  // TikTok uses both 'TikTok' (newer) and 'musically' (legacy).
  if (/TikTok|musically/i.test(ua)) return 'tiktok';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  if (/Twitter/i.test(ua)) return 'twitter';
  if (/LinkedIn/i.test(ua)) return 'linkedin';
  if (/Pinterest/i.test(ua)) return 'pinterest';
  return null;
}

export function isInAppBrowser(): boolean {
  return detectInAppBrowser() !== null;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/** Returns the absolute URL of the current page for "open here" instructions. */
export function getCurrentPageUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

/** Pretty name for the detected in-app browser, for user-facing copy. */
export function inAppBrowserDisplayName(kind: InAppBrowserKind): string {
  switch (kind) {
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'snapchat':
      return 'Snapchat';
    case 'twitter':
      return 'X';
    case 'linkedin':
      return 'LinkedIn';
    case 'pinterest':
      return 'Pinterest';
    default:
      return 'this app';
  }
}

/** Name of the user's real (target) browser to send them to. */
export function targetBrowserName(): string {
  if (isIos()) return 'Safari';
  if (isAndroid()) return 'Chrome';
  return 'your browser';
}
